/**
 * MarketComments — community discussion on a fair-launch market (Gate 8).
 *
 * - Lists top-level comments, newest first.
 * - Connected wallet can post; requires nonce auth (no special creator role).
 * - Author can soft-delete their own comment.
 * - Refreshes automatically after posting or deleting.
 */

import { useState, useEffect, useCallback } from "react";
import { useWallet } from "../wallet/WalletContext";
import { Card } from "./ui";

const API_BASE = "";
const MAX_BODY = 500;

// ── Types ─────────────────────────────────────────────────────────────────────

interface Comment {
  id: string;
  chainId: number;
  tokenAddress: string;
  authorAddress: string;
  body: string;
  parentId: string | null;
  deletedAt: string | null;
  createdAt: string;
}

interface Props {
  chainId: number;
  tokenAddress: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function shortAddr(a: string) {
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (days  > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins  > 0) return `${mins}m ago`;
  return "just now";
}

// ── Main component ────────────────────────────────────────────────────────────

export function MarketComments({ chainId, tokenAddress }: Props) {
  const { address: connectedAddress, walletClient } = useWallet();

  const [comments, setComments]     = useState<Comment[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [body, setBody]             = useState("");
  const [posting, setPosting]       = useState(false);
  const [postError, setPostError]   = useState<string | null>(null);

  // ── Fetch ────────────────────────────────────────────────────────────────

  const fetchComments = useCallback(async () => {
    setError(null);
    try {
      const r = await fetch(
        `${API_BASE}/api/fair-launch/comments?chainId=${chainId}&tokenAddress=${tokenAddress}`,
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json() as { comments?: Comment[] };
      setComments(data.comments ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load comments");
    } finally {
      setLoading(false);
    }
  }, [chainId, tokenAddress]);

  useEffect(() => { void fetchComments(); }, [fetchComments]);

  // ── Post ─────────────────────────────────────────────────────────────────

  async function postComment() {
    if (!connectedAddress || !walletClient || !body.trim()) return;
    setPosting(true);
    setPostError(null);

    try {
      // 1. Request nonce.
      const nonceRes = await fetch(`${API_BASE}/api/auth/nonce`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          wallet:          connectedAddress,
          action:          "post_comment",
          chainId,
          contractAddress: tokenAddress,
        }),
      });
      if (!nonceRes.ok) throw new Error("Failed to get auth nonce");
      const { nonce, message } = await nonceRes.json() as { nonce: string; message: string };

      // 2. Sign with wallet.
      const signature = await walletClient.signMessage({ account: connectedAddress as `0x${string}`, message });

      // 3. Post comment.
      const postRes = await fetch(`${API_BASE}/api/fair-launch/comments`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          chainId,
          tokenAddress,
          body: body.trim(),
          nonce,
          signature,
          walletAddress: connectedAddress,
        }),
      });

      if (!postRes.ok) {
        const err = await postRes.json() as { error?: string };
        throw new Error(err.error ?? `HTTP ${postRes.status}`);
      }

      setBody("");
      await fetchComments();
    } catch (e) {
      setPostError(e instanceof Error ? e.message : "Failed to post comment");
    } finally {
      setPosting(false);
    }
  }

  // ── Delete ───────────────────────────────────────────────────────────────

  async function deleteComment(comment: Comment) {
    if (!connectedAddress || !walletClient) return;
    if (!window.confirm("Delete this comment?")) return;

    try {
      const nonceRes = await fetch(`${API_BASE}/api/auth/nonce`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          wallet:          connectedAddress,
          action:          "delete_comment",
          chainId,
          contractAddress: tokenAddress,
        }),
      });
      if (!nonceRes.ok) throw new Error("Failed to get auth nonce");
      const { nonce, message } = await nonceRes.json() as { nonce: string; message: string };
      const signature = await walletClient.signMessage({ account: connectedAddress as `0x${string}`, message });

      const delRes = await fetch(`${API_BASE}/api/fair-launch/comments/${comment.id}`, {
        method:  "DELETE",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ nonce, signature, walletAddress: connectedAddress }),
      });

      if (!delRes.ok) {
        const err = await delRes.json() as { error?: string };
        alert(err.error ?? "Failed to delete comment");
        return;
      }

      await fetchComments();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to delete comment");
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Card className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-sm">
          Community {comments.length > 0 && <span className="text-muted font-normal">({comments.length})</span>}
        </h3>
      </div>

      {/* Compose box */}
      {connectedAddress ? (
        <div className="space-y-2">
          <textarea
            value={body}
            onChange={e => setBody(e.target.value.slice(0, MAX_BODY))}
            placeholder="Say something about this token…"
            rows={3}
            className="w-full rounded-lg bg-white/5 border border-line/30 px-3 py-2 text-sm text-ink placeholder:text-muted/40 resize-none focus:outline-none focus:border-plasma/50 transition-colors"
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] text-muted/50">{body.length}/{MAX_BODY}</span>
            <button
              onClick={postComment}
              disabled={posting || !body.trim()}
              className="px-4 py-1.5 rounded-lg bg-plasma text-bg text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
            >
              {posting ? "Posting…" : "Post"}
            </button>
          </div>
          {postError && <p className="text-xs text-red-400">{postError}</p>}
        </div>
      ) : (
        <p className="text-xs text-muted/60">Connect your wallet to join the discussion.</p>
      )}

      {/* Comment list */}
      {loading ? (
        <div className="py-4 text-center text-xs text-muted/50">Loading comments…</div>
      ) : error ? (
        <div className="py-4 text-center text-xs text-red-400">{error}</div>
      ) : comments.length === 0 ? (
        <div className="py-4 text-center text-xs text-muted/50">No comments yet. Be the first.</div>
      ) : (
        <div className="space-y-3">
          {comments.map(c => (
            <CommentRow
              key={c.id}
              comment={c}
              connectedAddress={connectedAddress ?? null}
              onDelete={() => deleteComment(c)}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

// ── CommentRow ────────────────────────────────────────────────────────────────

function CommentRow({
  comment,
  connectedAddress,
  onDelete,
}: {
  comment: Comment;
  connectedAddress: string | null;
  onDelete: () => void;
}) {
  const isDeleted = !!comment.deletedAt;
  const isOwner = !isDeleted &&
    connectedAddress?.toLowerCase() === comment.authorAddress.toLowerCase();

  return (
    <div className={`flex gap-3 ${isDeleted ? "opacity-40" : ""}`}>
      {/* Avatar */}
      <div className="w-7 h-7 rounded-full bg-white/10 shrink-0 flex items-center justify-center text-[10px] text-muted font-mono">
        {comment.authorAddress.slice(2, 4).toUpperCase()}
      </div>

      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-xs text-muted/80">
            {isDeleted ? "0x0000…0000" : shortAddr(comment.authorAddress)}
          </span>
          <span className="text-[10px] text-muted/40">{relativeTime(comment.createdAt)}</span>
          {isOwner && (
            <button
              onClick={onDelete}
              className="ml-auto text-[10px] text-muted/30 hover:text-red-400 transition-colors"
              title="Delete comment"
            >
              Delete
            </button>
          )}
        </div>
        <p className="text-sm text-ink/90 break-words whitespace-pre-wrap">
          {isDeleted ? "[deleted]" : comment.body}
        </p>
      </div>
    </div>
  );
}
